/**
 * MEDICATION PAGE COMPONENT - Patient Medication Management Interface
 * 
 * This component provides a comprehensive interface for managing patient medications
 * in the medical monitoring system. It handles CRUD operations for medication records,
 * including dosage tracking, scheduling, and medical history management.
 * 
 * Key Features:
 * - Complete medication lifecycle management (add, edit, delete, view)
 * - Patient-specific medication filtering and organization
 * - Dosage and frequency tracking for medical compliance
 * - Medication history and timeline management
 * - Form validation and error handling
 * - Responsive design with modal interfaces
 * 
 * Medical Data Management:
 * - Medication names and pharmaceutical information
 * - Dosage tracking with units and measurements
 * - Frequency scheduling (daily, weekly, as needed)
 * - Start and end dates for medication courses
 * - Medical notes and special instructions
 * - Patient-specific medication profiles
 * 
 * User Interface:
 * - Medication list with search and filter capabilities
 * - Add/Edit medication modal forms
 * - Patient information display
 * - Navigation and breadcrumb support
 * - Loading states and error handling
 */

// React core imports for component functionality and state management
import React, { useState, useEffect } from 'react';

// API imports for medication management operations
import { Medication, loadMedications, saveMedication, updateMedication, deleteMedication, MedicationFormData, defaultMedication } from '../api/medication';
import { getPatientInfo } from '../api/database';

// Page navigation type definition for application routing
type Page = 'login' | 'register' | 'dashboard' | 'history' | 'settings' | 'consent' | 'thresholds' | 'patients' | 'medication';

/**
 * MEDICATION PAGE PROPS INTERFACE
 * 
 * Defines the props structure for the MedicationPage component,
 * including navigation callbacks and patient identification.
 */
type MedicationPageProps = {
  onNavigate: (page: Page) => void;  // Callback function for page navigation
  patId?: string;                   // Optional patient ID for medication filtering
  previousPage?: any;       // Previous page for back navigation
  isDemo?: boolean;                 // Demo mode flag for hardcoded data
};

const MedicationPage: React.FC<MedicationPageProps> = ({ onNavigate, patId, previousPage, isDemo = false }) => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [patientInfo, setPatientInfo] = useState<{ pat_id: string; first_name: string; last_name: string; dev_id: string } | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [formData, setFormData] = useState<MedicationFormData>(defaultMedication);
  const [dosageAmount, setDosageAmount] = useState<string>(''); // Left input: numeric dosage
  const [dosageUnit, setDosageUnit] = useState<string>('');     // Right input: dosage unit text
  const [frequencyAmount, setFrequencyAmount] = useState<string>(''); // Left input: numeric frequency
  const [frequencyUnit, setFrequencyUnit] = useState<string>('');     // Right input: frequency text (e.g., day, 2 days)

  // Load medications and patient info when component mounts or patId changes
  useEffect(() => {
    const loadPatientData = async () => {
      try {
        setLoading(true);
        
        if (isDemo) {
          // Load demo data
          console.log('[MedicationPage] Loading demo data');
          
          // Demo medications (same as in Dashboard)
          const demoMedications: Medication[] = [
            {
              med_id: 1,
              pat_id: 999,
              name: 'Aspirin',
              starting_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '100mg',
              frequency: 'Once daily',
              notes: 'Take with food to prevent stomach irritation'
            },
            {
              med_id: 2,
              pat_id: 999,
              name: 'Metformin',
              starting_date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '500mg',
              frequency: 'Twice daily',
              notes: 'Monitor blood glucose levels regularly'
            },
            {
              med_id: 3,
              pat_id: 999,
              name: 'Lisinopril',
              starting_date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '10mg',
              frequency: 'Once daily',
              notes: 'Monitor blood pressure and kidney function'
            },
            {
              med_id: 4,
              pat_id: 999,
              name: 'Atorvastatin',
              starting_date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '20mg',
              frequency: 'Once daily',
              notes: 'Monitor liver function and muscle pain'
            },
            {
              med_id: 5,
              pat_id: 999,
              name: 'Omeprazole',
              starting_date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
              ending_date: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
              dosage: '20mg',
              frequency: 'Once daily',
              notes: 'Take before breakfast for acid reflux'
            }
          ];
          
          // Demo patient info
          const demoPatientInfo = {
            pat_id: '999',
            first_name: 'Demo',
            last_name: 'User',
            dev_id: 'DEMO-001'
          };
          
          setMedications(demoMedications);
          setPatientInfo(demoPatientInfo);
          console.log('[MedicationPage] Demo data loaded:', { demoMedications, demoPatientInfo });
        } else {
          const currentPatId = patId || localStorage.getItem('selectedPatId');
          if (currentPatId) {
            console.log('[MedicationPage] Loading data for pat_id:', currentPatId);
            
            // Load patient info and medications in parallel
            const [patientMedications, patientData] = await Promise.all([
              loadMedications(currentPatId),
              getPatientInfo(currentPatId)
            ]);
            
            setMedications(patientMedications);
            setPatientInfo(patientData);
            console.log('[MedicationPage] Data loaded:', { patientMedications, patientData });
          } else {
            console.log('[MedicationPage] No pat_id available');
            setMedications([]);
            setPatientInfo(null);
          }
        }
      } catch (error) {
        console.error('[MedicationPage] Error loading data:', error);
        setMedications([]);
        setPatientInfo(null);
      } finally {
        setLoading(false);
      }
    };

    loadPatientData();
  }, [patId, isDemo]);

  const openAddModal = () => {
    setFormData(defaultMedication);
    setDosageAmount('');
    setDosageUnit('');
    setFrequencyAmount('');
    setFrequencyUnit('');
    setIsAddModalOpen(true);
  };

  const openEditModal = (medication: Medication) => {
    // Helper to normalize any stored date string to YYYY-MM-DD for <input type="date">
    const toDateInputValue = (value?: string) => {
      if (!value) return '';
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      // If ISO string with time, or any other parsable date, convert
      const d = new Date(value);
      if (isNaN(d.getTime())) {
        return '';
      }
      return d.toISOString().split('T')[0];
    };

    setEditingMedication(medication);
    setFormData({
      name: medication.name,
      dosage: medication.dosage || '',
      frequency: medication.frequency || '',
      starting_date: toDateInputValue(medication.starting_date),
      ending_date: toDateInputValue(medication.ending_date),
      notes: medication.notes || ''
    });
    // Split existing dosage into numeric amount and unit text (e.g. "5 tablets")
    if (medication.dosage) {
      const match = medication.dosage.match(/^(\d+(?:[.,]\d+)?)[\s]*(.*)$/);
      if (match) {
        setDosageAmount(match[1]);
        setDosageUnit(match[2] || '');
      } else {
        // Fallback: put everything into unit text if it doesn't start with a number
        setDosageAmount('');
        setDosageUnit(medication.dosage);
      }
    } else {
      setDosageAmount('');
      setDosageUnit('');
    }

    // Split existing frequency into numeric amount and unit text based on "X per Y" pattern
    if (medication.frequency) {
      const freqMatch = medication.frequency.match(/^(\d+)\s+per\s+(.*)$/i);
      if (freqMatch) {
        setFrequencyAmount(freqMatch[1]);
        setFrequencyUnit(freqMatch[2] || '');
      } else {
        // Fallback: put everything into unit text if it doesn't match the "X per Y" pattern
        setFrequencyAmount('');
        setFrequencyUnit(medication.frequency);
      }
    } else {
      setFrequencyAmount('');
      setFrequencyUnit('');
    }
    setIsEditModalOpen(true);
  };

  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditingMedication(null);
    setFormData(defaultMedication);
    setDosageAmount('');
    setDosageUnit('');
    setFrequencyAmount('');
    setFrequencyUnit('');
  };

  const handleSave = async () => {
    const currentPatId = patId || localStorage.getItem('selectedPatId');
    if (!currentPatId) return;

    // Combine dosage amount and unit into a single dosage string before saving
    const combinedDosage = dosageAmount && dosageUnit
      ? `${dosageAmount} ${dosageUnit}`
      : (dosageAmount || dosageUnit || formData.dosage || '');

    // Combine frequency amount and unit into a single frequency string before saving
    const combinedFrequency = frequencyAmount && frequencyUnit
      ? `${frequencyAmount} per ${frequencyUnit}`
      : (frequencyAmount || frequencyUnit || formData.frequency || '');

    const payload: MedicationFormData = {
      ...formData,
      dosage: combinedDosage,
      frequency: combinedFrequency
    };

    let success = false;
    if (editingMedication) {
      success = await updateMedication(editingMedication.med_id.toString(), payload, currentPatId);
    } else {
      success = await saveMedication(payload, currentPatId);
    }

    if (success) {
      // Reload medications
      const updatedMedications = await loadMedications(currentPatId);
      setMedications(updatedMedications);
      closeModals();
    } else {
      alert('Failed to save medication. Please try again.');
    }
  };

  const handleDelete = async (medicationId: string) => {
    if (!window.confirm('Are you sure you want to delete this medication?')) return;

    const currentPatId = patId || localStorage.getItem('selectedPatId');
    if (!currentPatId) return;

    const success = await deleteMedication(medicationId, currentPatId);
    if (success) {
      // Reload medications
      const updatedMedications = await loadMedications(currentPatId);
      setMedications(updatedMedications);
    } else {
      alert('Failed to delete medication. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isActive = (medication: Medication) => {
    const now = new Date();
    const startDate = new Date(medication.starting_date);
    const endDate = medication.ending_date ? new Date(medication.ending_date) : null;
    
    return now >= startDate && (!endDate || now <= endDate);
  };

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => onNavigate(previousPage || 'dashboard')} aria-label="Back">←</button>
          <h1>Medication</h1>
        </div>
        <div />
      </header>

      <div className="history-container thresholds-narrow">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div>Loading patient data...</div>
          </div>
        ) : (
          <>
            {/* Patient Information Header */}
            {patientInfo && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {patientInfo.first_name.charAt(0).toUpperCase()}{patientInfo.last_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '1.5rem', 
                    fontWeight: '600', 
                    color: '#2c3e50' 
                  }}>
                    {patientInfo.first_name} {patientInfo.last_name}
                  </h2>
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    fontSize: '0.9rem', 
                    color: '#6c757d' 
                  }}>
                    <span>
                      <strong>Patient ID:</strong> {patientInfo.pat_id}
                    </span>
                    <span>
                      <strong>Device ID:</strong> {patientInfo.dev_id}
                    </span>
                  </div>
                </div>
                <div style={{
                  color: '#28a745',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  backgroundColor: '#d4edda',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #c3e6cb'
                }}>
                  Medication Management
                </div>
              </div>
            )}

            {/* Add Medication Button - disabled in demo mode */}
            {!isDemo && (
              <div style={{ marginBottom: '24px' }}>
                <button
                  onClick={openAddModal}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#5a6fd8';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  <span>💊</span>
                  Add New Medication
                </button>
              </div>
            )}
            
            {/* Demo mode info message */}
            {isDemo && (
              <div style={{ 
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                color: '#1565c0',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>ℹ️</span>
                <span>Demo Mode: Viewing sample medications. Editing is disabled in demo mode.</span>
              </div>
            )}

            {/* Medications List */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e9ecef',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '1.2rem',
                fontWeight: '600',
                color: '#495057',
                borderBottom: '2px solid #f8f9fa',
                paddingBottom: '12px'
              }}>
                Current Medications
              </h3>
              
              {medications.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {medications.map((medication) => (
                     <div
                       key={medication.med_id}
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: isActive(medication) ? '#f8f9fa' : '#fff5f5',
                        borderLeft: `4px solid ${isActive(medication) ? '#28a745' : '#dc3545'}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: '1.1rem', 
                            fontWeight: '600', 
                            color: '#2c3e50',
                            marginBottom: '8px'
                          }}>
                            {medication.name}
                          </div>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#6c757d',
                            marginBottom: '4px'
                          }}>
                            <strong>Dosage:</strong> {medication.dosage}
                          </div>
                          <div style={{ 
                            fontSize: '0.9rem', 
                            color: '#6c757d',
                            marginBottom: '4px'
                          }}>
                            <strong>Frequency:</strong> {medication.frequency}
                          </div>
                           <div style={{ 
                             fontSize: '0.9rem', 
                             color: '#6c757d',
                             marginBottom: '4px'
                           }}>
                             <strong>Start Date:</strong> {formatDate(medication.starting_date)}
                           </div>
                           {medication.ending_date && (
                             <div style={{ 
                               fontSize: '0.9rem', 
                               color: '#6c757d',
                               marginBottom: '4px'
                             }}>
                               <strong>End Date:</strong> {formatDate(medication.ending_date)}
                             </div>
                           )}
                          {medication.notes && (
                            <div style={{ 
                              fontSize: '0.9rem', 
                              color: '#6c757d',
                              marginTop: '8px',
                              fontStyle: 'italic'
                            }}>
                              <strong>Notes:</strong> {medication.notes}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                          <button
                            onClick={() => openEditModal(medication)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #667eea',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              color: '#667eea',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#667eea';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.color = '#667eea';
                            }}
                          >
                            Edit
                          </button>
                           <button
                             onClick={() => handleDelete(medication.med_id.toString())}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #dc3545',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              color: '#dc3545',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.color = '#dc3545';
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem 2rem',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>💊</div>
                  <h4 style={{ margin: '0 0 8px 0', color: '#95a5a6' }}>No medications found</h4>
                  <p style={{ margin: 0, fontSize: '0.9rem' }}>
                    Add the first medication for this patient.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Medication Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <div 
          className="modal-backdrop" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
        >
          <div 
            className="modal" 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'hidden',
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            <div 
              className="modal-header"
              style={{
                padding: '24px 24px 16px 24px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.5rem', 
                  fontWeight: '600', 
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '1.8rem' }}>💊</span>
                  {isAddModalOpen ? 'Add New Medication' : 'Edit Medication'}
                </h3>
              </div>
              <button 
                className="modal-close" 
                onClick={closeModals}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.8rem',
                  color: '#bdc3c7',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                ×
              </button>
            </div>
            
            <div 
              className="modal-body"
              style={{
                padding: '20px 24px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    color: '#495057',
                    marginBottom: '6px'
                  }}>
                    Medication Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    color: '#495057',
                    marginBottom: '6px'
                  }}>
                    Dosage *
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                      width: '100%'
                    }}
                  >
                    <input
                      type="text"
                      value={dosageAmount}
                      onChange={(e) => {
                        // Allow only numbers, comma and dot
                        const cleaned = e.target.value.replace(/[^0-9.,]/g, '');
                        setDosageAmount(cleaned);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      placeholder="e.g., 5"
                    />
                    <input
                      type="text"
                      value={dosageUnit}
                      onChange={(e) => {
                        // Allow only letters (English & Greek) and spaces
                        const cleaned = e.target.value.replace(/[^A-Za-zΑ-Ωα-ωάέίήύόώ\s]/g, '');
                        setDosageUnit(cleaned);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                      placeholder="e.g., tablets"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    color: '#495057',
                    marginBottom: '6px'
                  }}>
                    Frequency *
                  </label>
                  {/* One big visual box with left numeric, middle fixed "per", right text */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      alignItems: 'stretch',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      fontSize: '1rem',
                      width: '100%'
                    }}
                  >
                    <input
                      type="text"
                      value={frequencyAmount}
                      onChange={(e) => {
                        // Allow only numbers
                        const cleaned = e.target.value.replace(/[^0-9]/g, '');
                        setFrequencyAmount(cleaned);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                      placeholder="e.g., 1"
                    />
                    <div
                      style={{
                        padding: '10px 16px',
                        borderLeft: '1px solid #e9ecef',
                        borderRight: '1px solid #e9ecef',
                        backgroundColor: '#f8f9fa',
                        color: '#000',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.05rem',
                        fontWeight: 600
                      }}
                    >
                      per
                    </div>
                    <input
                      type="text"
                      value={frequencyUnit}
                      onChange={(e) => {
                        // Allow numbers + letters (English & Greek) + spaces
                        const cleaned = e.target.value.replace(/[^0-9A-Za-zΑ-Ωα-ωάέίήύόώ\s]/g, '');
                        setFrequencyUnit(cleaned);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                      placeholder="e.g., day, 2 days"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '500', 
                      color: '#495057',
                      marginBottom: '6px'
                    }}>
                      Start Date *
                    </label>
                     <input
                       type="date"
                       value={formData.starting_date}
                       onChange={(e) => setFormData({ ...formData, starting_date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: '0.9rem', 
                      fontWeight: '500', 
                      color: '#495057',
                      marginBottom: '6px'
                    }}>
                      End Date
                    </label>
                     <input
                       type="date"
                       value={formData.ending_date}
                       onChange={(e) => setFormData({ ...formData, ending_date: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        transition: 'all 0.2s ease',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.9rem', 
                    fontWeight: '500', 
                    color: '#495057',
                    marginBottom: '6px'
                  }}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      resize: 'vertical'
                    }}
                    placeholder="Additional notes about the medication..."
                  />
                </div>
              </div>
            </div>
            
            <div 
              className="modal-actions"
              style={{
                padding: '16px 24px 24px 24px',
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                backgroundColor: '#fafbfc'
              }}
            >
              <button 
                className="btn-secondary" 
                onClick={closeModals}
                style={{
                  padding: '12px 24px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSave}
                 disabled={!formData.name || !formData.starting_date}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                   opacity: (!formData.name || !formData.starting_date) ? 0.5 : 1
                }}
              >
                {isAddModalOpen ? 'Add Medication' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationPage;
